import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import ParagraphTool from '@editorjs/paragraph';
import List from '@editorjs/list';
import { useLanguage } from '../context/LanguageContext';
import { articleAPI, attorneyAPI } from '../services/api';
import { PRACTICE_AREA_KEYS } from '../constants/practiceAreas';
import QuickAnswerTool from '../components/editor/tools/QuickAnswerTool';
import DecisionTreeTool from '../components/editor/tools/DecisionTreeTool';
import FeeCalloutTool from '../components/editor/tools/FeeCalloutTool';
import DocListTool from '../components/editor/tools/DocListTool';
import MistakesTool from '../components/editor/tools/MistakesTool';
import FactBoxTool from '../components/editor/tools/FactBoxTool';
import '../components/editor/tools/editorTools.css';

const STATUS_LABELS = {
  draft: { label: 'Қоралама', color: '#718096', bg: '#f1f5f9' },
  pending_review: { label: 'Кўриб чиқилмоқда', color: '#b7791f', bg: '#fefcbf' },
  published: { label: 'Чоп этилган', color: '#276749', bg: '#c6f6d5' },
  rejected: { label: 'Рад этилган', color: '#c53030', bg: '#fed7d7' },
  pending_edit: { label: 'Таҳрир кўриб чиқилмоқда', color: '#b7791f', bg: '#fefcbf' },
};

const styles = {
  container: { maxWidth: '900px', margin: '0 auto', padding: '2rem' },
  header: { marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' },
  title: { fontSize: '1.75rem', fontWeight: '700', color: '#1a365d', marginBottom: '0.25rem' },
  section: { backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '1.5rem' },
  label: { fontSize: '0.85rem', fontWeight: '600', color: '#4a5568', marginBottom: '0.4rem', display: 'block' },
  input: { padding: '0.75rem 1rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem', outline: 'none', width: '100%', boxSizing: 'border-box' },
  textarea: { padding: '0.75rem 1rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem', outline: 'none', minHeight: '90px', resize: 'vertical', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  row: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' },
  field: { marginBottom: '1rem' },
  editorBox: { border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', minHeight: '300px' },
  actions: { display: 'flex', gap: '0.75rem', marginTop: '1.5rem', flexWrap: 'wrap' },
  primaryBtn: { backgroundColor: '#FFD700', color: '#0d1f17', padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '0.95rem' },
  secondaryBtn: { backgroundColor: '#edf2f7', color: '#2d3748', padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.95rem' },
  badge: { display: 'inline-block', padding: '0.3rem 0.85rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600' },
  errorBox: { backgroundColor: '#fed7d7', color: '#c53030', padding: '0.75rem 1rem', borderRadius: '8px', fontSize: '0.9rem', marginBottom: '1rem' },
  infoBox: { backgroundColor: '#fed7d7', color: '#c53030', padding: '0.75rem 1rem', borderRadius: '8px', fontSize: '0.9rem', marginBottom: '1.5rem' },
};

const ArticleEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [article, setArticle] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [readTimeMin, setReadTimeMin] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tier, setTier] = useState(null);

  // Publishing (submit for review) is VIP-only; drafting stays open at any tier so nobody loses
  // work — fetched once so the submit button can show an upfront upgrade lock instead of letting
  // an attorney write a full article only to hit a 403 on submit.
  useEffect(() => {
    attorneyAPI.getSubscriptionStatus().then(res => setTier(res.data?.tier || 'free')).catch(() => setTier('free'));
  }, []);

  const editorRef = useRef(null);
  const editorContainerRef = useRef(null);
  const editorReadyRef = useRef(null);

  // Bootstrap: create a draft if visiting "new", otherwise load existing article
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (id) {
          const res = await articleAPI.getOne(id);
          if (!active) return;
          const data = res.data;
          setArticle(data);
          // For published articles with a pending edit, load the pending values into the form
          const useEditFields = data.status === 'published' && data.has_pending_edit;
          setTitle((useEditFields ? data.pending_edit_title : data.title) || '');
          setCategory(data.category || '');
          setExcerpt((useEditFields ? data.pending_edit_excerpt : data.excerpt) || '');
          setReadTimeMin((useEditFields ? data.pending_edit_read_time_min : data.read_time_min) || '');
        } else {
          const res = await articleAPI.create({ title: 'Янги мақола' });
          if (!active) return;
          navigate(`/attorney/articles/${res.data.id}/edit`, { replace: true });
          return;
        }
      } catch (err) {
        if (active) setError(err.response?.data?.error || 'Мақолани юклашда хатолик юз берди');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, navigate]);

  // Initialize Editor.js once the article data is loaded
  useEffect(() => {
    if (loading || !article || !editorContainerRef.current) return;

    const useEditFields = article.status === 'published' && article.has_pending_edit;
    const editorData = useEditFields
      ? (article.pending_edit_content?.blocks ? article.pending_edit_content : article.content)
      : (article.content?.blocks ? article.content : { blocks: [] });
    const editor = new EditorJS({
      holder: editorContainerRef.current,
      data: editorData,
      placeholder: 'Мақола матнини шу ерга ёзинг...',
      tools: {
        header: { class: Header, config: { levels: [2, 3, 4], defaultLevel: 2 } },
        paragraph: { class: ParagraphTool, inlineToolbar: true },
        list: { class: List, inlineToolbar: true },
        quickAnswer: QuickAnswerTool,
        decisionTree: DecisionTreeTool,
        feeCallout: FeeCalloutTool,
        docList: DocListTool,
        mistakes: MistakesTool,
        factBox: FactBoxTool,
      },
    });
    editorRef.current = editor;
    editorReadyRef.current = editor.isReady;

    return () => {
      editorReadyRef.current?.then(() => editor.destroy?.()).catch(() => {});
    };
  }, [loading, article?.id]);

  const isPublished = article?.status === 'published';
  const hasPendingEdit = article?.has_pending_edit;
  const isEditable = !article || ['draft', 'rejected'].includes(article.status) || isPublished;

  const buildPayload = async () => {
    await editorReadyRef.current;
    const content = await editorRef.current.save();
    return {
      title: title.trim(),
      category: category || null,
      excerpt: excerpt.trim(),
      read_time_min: readTimeMin ? parseInt(readTimeMin, 10) : null,
      content,
    };
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = await buildPayload();
      if (isPublished) {
        const res = await articleAPI.saveEdit(id, payload);
        setArticle(res.data);
      } else {
        const res = await articleAPI.update(id, payload);
        setArticle(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Сақлашда хатолик юз берди');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Сарлавҳани киритинг');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = await buildPayload();
      if (isPublished) {
        const res = await articleAPI.submitEdit(id, payload);
        setArticle(res.data);
      } else {
        await articleAPI.update(id, payload);
        const res = await articleAPI.submit(id);
        setArticle(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Юборишда хатолик юз берди');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={styles.container}>Юкланмоқда...</div>;
  }

  const statusInfo = article
    ? (hasPendingEdit ? STATUS_LABELS.pending_edit : STATUS_LABELS[article.status])
    : null;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{id ? 'Мақолани таҳрирлаш' : 'Янги мақола'}</h1>
          <Link to="/attorney/dashboard" style={{ color: '#3182ce', fontSize: '0.9rem', textDecoration: 'none' }}>
            ← Дашбордга қайтиш
          </Link>
        </div>
        {statusInfo && (
          <span style={{ ...styles.badge, color: statusInfo.color, backgroundColor: statusInfo.bg }}>
            {statusInfo.label}
          </span>
        )}
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {article?.status === 'rejected' && article.rejection_reason && (
        <div style={styles.infoBox}>
          <strong>Рад этилди.</strong> Сабаби: {article.rejection_reason}
        </div>
      )}

      {isPublished && article.edit_rejection_reason && !hasPendingEdit && (
        <div style={styles.infoBox}>
          <strong>Охирги таҳрир рад этилди.</strong> Сабаби: {article.edit_rejection_reason}
        </div>
      )}

      {isPublished && hasPendingEdit && (
        <div style={{ backgroundColor: '#fefcbf', border: '1px solid #f6d860', borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.9rem', color: '#92400e', marginBottom: '1.5rem' }}>
          Таҳрир кўриб чиқилмоқда. Тасдиқлангунча мақоланинг аслий нусхаси сайтда кўринади.
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.field}>
          <label style={styles.label}>Сарлавҳа</label>
          <input
            style={styles.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={!isEditable}
            placeholder="Мақола сарлавҳаси"
          />
        </div>

        <div style={styles.row}>
          <div>
            <label style={styles.label}>Категория</label>
            <select
              style={styles.input}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={!isEditable}
            >
              <option value="">Категорияни танланг</option>
              {PRACTICE_AREA_KEYS.map((key) => (
                <option key={key} value={key}>{t(`practiceAreas.${key}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={styles.label}>Ўқиш вақти (дақиқа)</label>
            <input
              type="number"
              min="1"
              style={styles.input}
              value={readTimeMin}
              onChange={(e) => setReadTimeMin(e.target.value)}
              disabled={!isEditable}
            />
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Қисқа таъриф (excerpt)</label>
          <textarea
            style={styles.textarea}
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            disabled={!isEditable}
            placeholder="Мақола рўйхатида ва SEO учун кўрсатиладиган қисқа таъриф"
          />
        </div>
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Мақола матни</label>
        <div style={styles.editorBox} ref={editorContainerRef} translate="no" className="notranslate" />
      </div>

      {isEditable && tier !== null && tier !== 'vip' && (
        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', padding: '0.85rem 1rem', fontSize: '0.9rem', color: '#92400e', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <span>🔒 Мақолани кўриб чиқишга юбориш учун VIP обунаси керак. Қоралама сифатида сақлашингиз мумкин.</span>
          <Link to="/subscription" style={{ backgroundColor: '#f59e0b', color: 'white', padding: '0.4rem 1rem', borderRadius: '8px', textDecoration: 'none', fontSize: '0.85rem', fontWeight: '700', whiteSpace: 'nowrap' }}>
            VIP'га ўтиш
          </Link>
        </div>
      )}

      {isEditable && (
        <div style={styles.actions}>
          {!hasPendingEdit && (
            <button style={styles.secondaryBtn} onClick={handleSaveDraft} disabled={saving}>
              {saving ? 'Сақланмоқда...' : isPublished ? 'Таҳрирни сақлаш' : 'Сақлаш (қоралама)'}
            </button>
          )}
          {tier === 'vip' && (
            <button
              style={{ ...styles.primaryBtn, ...(hasPendingEdit ? { backgroundColor: '#a0aec0', cursor: 'not-allowed' } : {}) }}
              onClick={handleSubmit}
              disabled={saving || hasPendingEdit}
            >
              {saving ? 'Юборилмоқда...' : isPublished ? 'Таҳрирни кўриб чиқишга юбориш' : 'Кўриб чиқишга юбориш'}
            </button>
          )}
          {article?.slug && (
            <a href={`/legal-topics/${article.slug}`} target="_blank" rel="noopener noreferrer" style={{ ...styles.secondaryBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              Сайтда кўриш →
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default ArticleEditor;
